import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { needsSignupResume } from '../utils/needsSignupResume';

interface OnboardingGateProps {
  children: ReactNode;
}

/**
 * Synchronously intercepts navigation for authenticated users with incomplete
 * onboarding, redirecting them to /signup.
 *
 * Step restoration is handled entirely by the useSignupStep hook inside
 * UniversalSignup — it reads from AuthContext.user (post-auth) or from
 * /auth/signup-status/:user_id (pre-auth), so no sessionStorage writes are
 * needed here.
 */
const OnboardingGate = ({ children }: OnboardingGateProps) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // While auth is initializing, show a loader (null looked like a blank crash).
  // AuthContext keeps isLoading=true until GET /users/me finishes.
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#F8F7EC] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#7E8D4C] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-[#6b6358]">Loading…</p>
        </div>
      </div>
    );
  }

  // Not logged in → let normal routes render
  if (!isAuthenticated || !user) return <>{children}</>;

  // Admins bypass onboarding entirely
  if (user.role === 'admin') return <>{children}</>;

  // Already on /signup → don't redirect (prevents redirect loops)
  if (location.pathname === '/signup') return <>{children}</>;

  // Invite-accept pages must always render so they can save the token to
  // localStorage before redirecting. If OnboardingGate fires a Navigate here
  // first, the page never mounts and the token is silently lost.
  if (
    location.pathname === '/invite/accept' ||
    location.pathname === '/family/share/accept' ||
    location.pathname === '/claim' // claim flow is public and manages its own auth
  ) {
    return <>{children}</>;
  }

  if (!needsSignupResume(user)) {
    return <>{children}</>;
  }

  return <Navigate to="/signup" replace />;
};

export default OnboardingGate;
